import type { AppData, Card, CardState, DeckId, Settings } from '../../types';

export function retentionRate(history: AppData['history'], days = 30, deckId: DeckId | null = null) {
  const since = Date.now() - days * 86400000;
  const items = history.filter((entry) => entry.ts >= since && (!deckId || entry.deckId === deckId));
  const reviews = items.filter((entry) => entry.prevState === 'review');
  if (!reviews.length) return null;
  return reviews.filter((entry) => entry.q >= 3).length / reviews.length;
}

export function maturityDistribution(cards: Card[], states: Record<string, CardState>) {
  const result = { neu: 0, lernen: 0, jung: 0, reif: 0, geleakt: 0 };
  for (const card of cards) {
    const state = states[card.id];
    if (!state || state.state === 'new') result.neu += 1;
    else if (state.state === 'learning') result.lernen += 1;
    else if ((state.lapses || 0) > 0 && state.interval < 7) result.geleakt += 1;
    else if (state.interval < 21) result.jung += 1;
    else result.reif += 1;
  }
  return result;
}

export function chapterPerformance(cards: Card[], states: Record<string, CardState>, history: AppData['history'], deckId: DeckId) {
  const result: Record<string, { total: number; mature: number; reviews: number; correct: number; retention: number | null; maturePct: number }> = {};
  for (const card of cards) {
    result[card.chapter] ??= { total: 0, mature: 0, reviews: 0, correct: 0, retention: null, maturePct: 0 };
    result[card.chapter].total += 1;
    const state = states[card.id];
    if (state?.state === 'review' && state.interval >= 21) result[card.chapter].mature += 1;
  }

  const cardChapter = Object.fromEntries(cards.map((card) => [card.id, card.chapter]));
  for (const entry of history) {
    if (entry.deckId !== deckId || entry.prevState !== 'review') continue;
    const chapter = cardChapter[entry.cardId];
    if (!chapter || !result[chapter]) continue;
    result[chapter].reviews += 1;
    if (entry.q >= 3) result[chapter].correct += 1;
  }

  for (const key of Object.keys(result)) {
    const row = result[key];
    row.retention = row.reviews > 0 ? row.correct / row.reviews : null;
    row.maturePct = row.total > 0 ? row.mature / row.total : 0;
  }
  return result;
}

export function chapterBoost(perf: ReturnType<typeof chapterPerformance>, settings: Settings) {
  if (!settings.boostWeak) return {};
  const boost: Record<string, number> = {};
  for (const key of Object.keys(perf)) {
    const row = perf[key];
    let score = 1;
    if (row.retention !== null && row.retention < 0.8) score += (0.8 - row.retention) * 4;
    if (row.maturePct < 0.3) score += 0.3 - row.maturePct;
    if (score > 1) boost[key] = score;
  }
  return boost;
}

export function heatmap(history: AppData['history'], days = 91) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  const buckets = new Array(days).fill(0);
  for (const entry of history) {
    const day = new Date(entry.ts);
    day.setHours(0, 0, 0, 0);
    const index = Math.floor((day.getTime() - start.getTime()) / 86400000);
    if (index >= 0 && index < days) buckets[index] += 1;
  }
  return { start, buckets };
}

export function dailyTarget(cards: Card[], states: Record<string, CardState>, examDateIso: string | null, settings: Settings) {
  if (!examDateIso) return { newPerDay: settings.maxNewPerDay || 20, daysUntilExam: null, newRemaining: cards.filter((c) => !states[c.id] || states[c.id].state === 'new').length, totalCards: cards.length, learnedCards: cards.length - cards.filter((c) => !states[c.id] || states[c.id].state === 'new').length };
  const exam = new Date(examDateIso);
  exam.setHours(23, 59, 59, 999);
  const days = Math.max(1, Math.ceil((exam.getTime() - Date.now()) / 86400000));
  const newCards = cards.filter((card) => !states[card.id] || states[card.id].state === 'new');
  const learningDays = Math.max(1, Math.floor(days * 0.75));
  return {
    newPerDay: Math.ceil(newCards.length / learningDays),
    daysUntilExam: days,
    newRemaining: newCards.length,
    totalCards: cards.length,
    learnedCards: cards.length - newCards.length,
  };
}

export function examMultiplier(examDateIso: string | null, settings: Settings) {
  if (!settings.examMode || !examDateIso) return 1;
  const days = Math.ceil((new Date(examDateIso).getTime() - Date.now()) / 86400000);
  if (days <= 1) return 0.3;
  if (days <= 3) return 0.5;
  if (days <= 7) return 0.7;
  if (days <= 14) return 0.9;
  return 1;
}

export function reviewsToday(history: AppData['history'], deckId: DeckId | null = null) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return history.filter((entry) => entry.ts >= today.getTime() && (!deckId || entry.deckId === deckId)).length;
}

/**
 * Counts unique cards that were first introduced (transitioned from 'new')
 * since the start of the local day. Used to enforce the daily new-card limit
 * across multiple study sessions on the same day.
 */
export function newCardsStudiedToday(history: AppData['history'], deckId: DeckId | null = null) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const since = today.getTime();
  const seen = new Set<string>();
  for (const entry of history) {
    if (entry.ts < since) continue;
    if (deckId && entry.deckId !== deckId) continue;
    if (entry.prevState !== 'new') continue;
    seen.add(`${entry.deckId}:${entry.cardId}`);
  }
  return seen.size;
}

export function dueTodayCount(cards: Card[], states: Record<string, CardState>) {
  const now = new Date();
  let due = 0;
  let learning = 0;
  for (const card of cards) {
    const state = states[card.id];
    if (!state || state.state === 'new' || !state.due || new Date(state.due) > now) continue;
    if (state.state === 'learning') learning += 1;
    else due += 1;
  }
  return { due, learning, total: due + learning };
}

export function averageEase(cards: Card[], states: Record<string, CardState>) {
  const eases = cards.map((card) => states[card.id]?.ease).filter((ease): ease is number => typeof ease === 'number');
  if (!eases.length) return null;
  return eases.reduce((sum, ease) => sum + ease, 0) / eases.length;
}

export function hardestCards(cards: Card[], states: Record<string, CardState>, limit = 8) {
  return cards
    .map((card) => {
      const state = states[card.id];
      const accuracy = state?.reviewCount ? state.correctCount / state.reviewCount : null;
      const score = (state?.lapses ?? 0) * 4 + (state?.reviewCount ?? 0) * (accuracy === null ? 0 : 1 - accuracy) + Math.max(0, 2.5 - (state?.ease ?? 2.5));
      return { card, state, accuracy, score };
    })
    .filter((item) => item.state && (item.state.lapses > 0 || item.state.reviewCount > 0 || item.state.ease < 2.3))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function studyPace(cards: Card[], states: Record<string, CardState>, examDateIso: string | null, settings: Settings) {
  const target = dailyTarget(cards, states, examDateIso, settings);
  const learnedPct = target.totalCards ? target.learnedCards / target.totalCards : 0;
  const mature = maturityDistribution(cards, states).reif;
  const maturePct = target.totalCards ? mature / target.totalCards : 0;
  return { ...target, learnedPct, mature, maturePct };
}

export interface SchedulePoint { day: number; ts: number; actual: number; ideal: number }

export interface ScheduleProgress {
  start: Date;
  end: Date;
  days: number;
  total: number;
  learned: number;
  idealAtToday: number;
  delta: number;
  todayIndex: number;
  hasExam: boolean;
  points: SchedulePoint[];
}

/**
 * Computes cumulative "cards first studied" per day for a deck and the matching
 * ideal-pace line from the start of learning to the exam date. Useful to show
 * whether the user is on, ahead of, or behind their planned schedule.
 */
export function scheduleProgress(
  cards: Card[],
  history: AppData['history'],
  deckId: DeckId,
  examDateIso: string | null,
): ScheduleProgress {
  const total = cards.length;
  const cardIds = new Set(cards.map((card) => card.id));
  const firstSeen = new Map<string, number>();
  for (const entry of history) {
    if (entry.deckId !== deckId || !cardIds.has(entry.cardId)) continue;
    const prev = firstSeen.get(entry.cardId);
    if (prev === undefined || entry.ts < prev) firstSeen.set(entry.cardId, entry.ts);
  }

  const now = Date.now();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const earliestSeen = firstSeen.size > 0 ? Math.min(...firstSeen.values()) : now;
  const start = new Date(Math.min(earliestSeen, now));
  start.setHours(0, 0, 0, 0);

  const hasExam = !!examDateIso;
  const end = new Date(examDateIso ?? now + 30 * 86400000);
  end.setHours(0, 0, 0, 0);
  if (end.getTime() < today.getTime()) end.setTime(today.getTime());

  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
  const actual: number[] = new Array(days).fill(0);
  for (const ts of firstSeen.values()) {
    const day = Math.floor((ts - start.getTime()) / 86400000);
    if (day >= 0 && day < days) actual[day] += 1;
  }
  for (let i = 1; i < days; i++) actual[i] += actual[i - 1];

  const todayIndex = Math.min(days - 1, Math.max(0, Math.floor((today.getTime() - start.getTime()) / 86400000)));
  const denom = Math.max(1, days - 1);
  const points: SchedulePoint[] = [];
  for (let i = 0; i < days; i++) {
    const ideal = (total * i) / denom;
    points.push({
      day: i,
      ts: start.getTime() + i * 86400000,
      actual: i <= todayIndex ? actual[i] : actual[todayIndex],
      ideal,
    });
  }
  const idealAtToday = points[todayIndex]?.ideal ?? 0;
  const learned = actual[todayIndex] ?? 0;
  return {
    start,
    end,
    days,
    total,
    learned,
    idealAtToday,
    delta: learned - idealAtToday,
    todayIndex,
    hasExam,
    points,
  };
}
